import React, { useEffect, useState } from "react";
import { Link, useLocation } from "react-router-dom";
import { Menu, X, Phone, MessageCircle, Calendar, ShieldCheck } from "lucide-react";
import { CONTACT } from "../data/contactDetails";

const NAV = [
  { label: "Conditions", to: "/conditions" },
  { label: "Articles", to: "/articles" },
  { label: "Clinical Insights", to: "/clinical-insights" },
  { label: "Ask a Question", to: "/ask-a-question" },
  // The former "For Doctors" entry is hidden from public navigation while
  // /doctors is unlaunched; the redirect to /ask-a-question is defined in App.js.
  { label: "About", to: "/about" },
  { label: "Contact", to: "/contact" },
];

export default function Layout({ children }) {
  const [open, setOpen] = useState(false);
  const [scrolled, setScrolled] = useState(false);
  const loc = useLocation();

  // Compact-sticky-header trigger. Passive listener + cleanup.
  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 40);
    onScroll();
    window.addEventListener("scroll", onScroll, { passive: true });
    return () => window.removeEventListener("scroll", onScroll);
  }, []);

  return (
    <div className="min-h-screen bg-bone text-ink flex flex-col">
      {/* WCAG · Skip to main content — visible only on keyboard focus */}
      <a
        href="#main"
        data-testid="skip-to-main"
        className="sr-only focus:not-sr-only focus:fixed focus:top-3 focus:left-3 focus:z-50 focus:bg-navy focus:text-white focus:px-4 focus:py-2 focus:text-sm focus:font-medium focus:shadow-lg"
      >
        Skip to main content
      </a>
      <header
        role="banner"
        data-scrolled={scrolled ? "true" : "false"}
        className="sticky top-0 z-40 bg-white/90 backdrop-blur-xl border-b border-line transition-[background-color,box-shadow] duration-300"
      >
        <div
          className={`max-w-7xl mx-auto px-4 sm:px-6 md:px-12 flex items-center justify-between gap-2 transition-[height] duration-300 ease-out ${
            scrolled ? "h-16 md:h-20" : "h-20 md:h-28"
          }`}
        >
          <Link to="/" data-testid="logo-link" aria-label="Dr Tania Gurdip Singh — Home" className="flex items-center gap-3 shrink-0 focus:outline-none focus-visible:ring-2 focus-visible:ring-navy rounded-sm">
            <img
              src="/brand/logo-wordmark.png"
              alt="Dr. Tania Gurdip Singh · High Risk Pregnancy"
              className={`w-auto select-none transition-[height] duration-300 ease-out ${
                scrolled ? "h-10 md:h-[64px]" : "h-14 md:h-[100px]"
              }`}
              width="240" height="160"
              draggable="false"
            />
          </Link>
          <nav className="hidden lg:flex items-center gap-7" aria-label="Primary">
            {NAV.map(n => (
              <Link
                key={n.to}
                to={n.to}
                data-testid={`nav-${n.label.toLowerCase().replace(/\s+/g,'-')}`}
                aria-current={loc.pathname.startsWith(n.to) ? "page" : undefined}
                className={`text-sm tracking-wide transition-colors focus:outline-none focus-visible:ring-2 focus-visible:ring-navy focus-visible:ring-offset-2 rounded-sm ${loc.pathname.startsWith(n.to) ? "text-navy font-semibold" : "text-stone2 hover:text-navy"}`}
              >
                {n.label}
              </Link>
            ))}
          </nav>
          <div className="hidden lg:flex items-center gap-3">
            <a href={CONTACT.primary.whatsapp} data-testid="header-whatsapp" aria-label={`Message us on WhatsApp: ${CONTACT.primary.display}`} className="text-sm text-terracotta hover:text-terracotta-hover font-medium flex items-center gap-1.5 focus:outline-none focus-visible:ring-2 focus-visible:ring-terracotta focus-visible:ring-offset-2 rounded-sm">
              <MessageCircle size={16} strokeWidth={1.5} aria-hidden="true"/> WhatsApp
            </a>
            <Link to="/contact?type=appointment" data-testid="header-book" className="border border-navy text-navy bg-transparent text-sm font-medium px-5 py-2.5 rounded-full hover:bg-navy hover:text-white transition-colors focus:outline-none focus-visible:ring-2 focus-visible:ring-navy focus-visible:ring-offset-2">
              Book a Consultation
            </Link>
          </div>
          <button
            data-testid="mobile-menu-toggle"
            className="lg:hidden text-navy p-2 -mr-2 focus:outline-none focus-visible:ring-2 focus-visible:ring-navy rounded-sm"
            onClick={() => setOpen(!open)}
            aria-label={open ? "Close menu" : "Open menu"}
            aria-expanded={open}
            aria-controls="mobile-nav"
          >
            {open ? <X aria-hidden="true"/> : <Menu aria-hidden="true"/>}
          </button>
        </div>
        {open && (
          <div id="mobile-nav" className="lg:hidden border-t border-line bg-white">
            <nav aria-label="Mobile" className="px-6 py-4 flex flex-col gap-3">
              {/* Home appears only in the mobile menu (and in the footer);
                  the desktop header intentionally omits it because the logo
                  already serves as the Home link. */}
              <Link
                to="/"
                onClick={()=>setOpen(false)}
                data-testid="mnav-home"
                aria-current={loc.pathname === "/" ? "page" : undefined}
                className="text-base text-ink py-2 border-b border-line focus:outline-none focus-visible:ring-2 focus-visible:ring-navy rounded-sm"
              >
                Home
              </Link>
              {NAV.map(n => (
                <Link
                  key={n.to} to={n.to}
                  onClick={()=>setOpen(false)}
                  data-testid={`mnav-${n.label.toLowerCase()}`}
                  aria-current={loc.pathname.startsWith(n.to) ? "page" : undefined}
                  className="text-base text-ink py-2 border-b border-line last:border-0 focus:outline-none focus-visible:ring-2 focus-visible:ring-navy rounded-sm"
                >
                  {n.label}
                </Link>
              ))}
            </nav>
          </div>
        )}
      </header>

      <main id="main" tabIndex={-1} className="flex-1 focus:outline-none">{children}</main>
      <MobileBottomBar />
      <Footer />
    </div>
  );
}

function DisclaimerStrip() {
  return (
    <div role="note" aria-label="Medical emergency notice" className="bg-warn text-[#9F1239] text-[11px] tracking-wide font-medium py-1.5 px-4 text-center border-b border-[#9F1239]/20">
      For medical emergencies, please call your local emergency services or nearest maternity hospital immediately. This platform is educational and does not replace clinical care.
    </div>
  );
}

function MobileBottomBar() {
  return (
    <nav aria-label="Quick contact" className="fixed bottom-0 left-0 right-0 z-40 bg-white border-t border-line shadow-[0_-4px_20px_rgba(0,0,0,0.05)] p-3 grid grid-cols-3 gap-2 lg:hidden">
      <a href={CONTACT.office.tel} data-testid="mbb-call" aria-label={`Call the office: ${CONTACT.office.display}`} className="border border-navy text-navy rounded-full py-2 text-sm font-medium flex items-center justify-center gap-1.5 focus:outline-none focus-visible:ring-2 focus-visible:ring-navy focus-visible:ring-offset-2">
        <Phone size={14} strokeWidth={1.5} aria-hidden="true"/> Call
      </a>
      <a href={CONTACT.primary.whatsapp} data-testid="mbb-whatsapp" aria-label={`Message on WhatsApp: ${CONTACT.primary.display}`} className="bg-terracotta text-white rounded-full py-2 text-sm font-medium flex items-center justify-center gap-1.5 focus:outline-none focus-visible:ring-2 focus-visible:ring-terracotta focus-visible:ring-offset-2">
        <MessageCircle size={14} strokeWidth={1.5} aria-hidden="true"/> WhatsApp
      </a>
      <Link to="/contact?type=appointment" data-testid="mbb-book" aria-label="Book a consultation" className="bg-navy text-white rounded-full py-2 text-sm font-medium flex items-center justify-center gap-1.5 focus:outline-none focus-visible:ring-2 focus-visible:ring-navy focus-visible:ring-offset-2">
        <Calendar size={14} strokeWidth={1.5} aria-hidden="true"/> Book
      </Link>
    </nav>
  );
}

function Footer() {
  return (
    <footer className="bg-navy-deep text-white/85 mt-24 pb-24 lg:pb-16">
      <div className="max-w-7xl mx-auto px-6 md:px-12 py-16 grid md:grid-cols-3 gap-10">
        <div>
          <div className="font-serif text-2xl text-white">Dr. Tania Gurdip Singh</div>
          <div className="mono text-[10px] uppercase tracking-[0.2em] text-prestige-soft mt-2">Senior High-Risk Pregnancy Consultant</div>
          <p className="text-sm mt-5 text-white/70 max-w-xs">Evidence-based, preventive care for high-risk pregnancy and recurrent pregnancy loss.</p>
          <p className="text-sm mt-4 text-white/70 max-w-xs" data-testid="footer-practice-line">New Delhi–based High-Risk Pregnancy Consultant · Online consultations · Dedicated High-Risk Pregnancy OPD in Ambala on one weekend day</p>
          <div className="mt-6 space-y-2 text-sm text-white/80" data-testid="footer-contact-numbers">
            <div>
              <div className="mono text-[10px] uppercase tracking-[0.2em] text-white/50">{CONTACT.primary.label}</div>
              <div className="mt-1 flex flex-wrap gap-x-4">
                <a href={CONTACT.primary.tel} data-testid="footer-primary-call" className="hover:text-prestige-soft">{CONTACT.primary.display}</a>
                <a href={CONTACT.primary.whatsapp} data-testid="footer-primary-whatsapp" className="hover:text-prestige-soft">WhatsApp</a>
              </div>
            </div>
            <div>
              <div className="mono text-[10px] uppercase tracking-[0.2em] text-white/50">{CONTACT.office.label}</div>
              <div className="mt-1">
                <a href={CONTACT.office.tel} data-testid="footer-office-call" className="hover:text-prestige-soft">{CONTACT.office.display}</a>
              </div>
            </div>
          </div>
          <div className="mt-6" data-testid="footer-new-delhi-clinic">
            <div className="mono text-[10px] uppercase tracking-[0.2em] text-white/50">New Delhi Clinic</div>
            <address className="not-italic text-sm mt-2 text-white/80 leading-relaxed max-w-xs">
              HIGH RISKK PREGNANCY CLINIC<br/>
              A-78A, Veer Savarkar Marg<br/>
              Opposite SBI Bank, Lajpat Nagar II<br/>
              New Delhi – 110024
            </address>
          </div>
        </div>
        <FooterColumn title="Explore" links={[
          {label: "Home", to: "/"},
          {label: "Conditions", to: "/conditions"},
          {label: "Articles", to: "/articles"},
          {label: "Clinical Insights", to: "/clinical-insights"},
          {label: "Ask a Question", to: "/ask-a-question"},
          {label: "About Dr Tania", to: "/about"},
          {label: "Contact", to: "/contact"},
        ]}/>
        <FooterColumn title="Consultation" links={[
          {label: "Book a Consultation", to: "/contact?type=appointment"},
          {label: "WhatsApp", href: CONTACT.primary.whatsapp},
        ]}/>
        <FooterColumn title="Legal" links={[
          {label: "Privacy & Consent", to: "/privacy"},
          {label: "Terms of Use", to: "/terms"},
          {label: "Medical Disclaimer", to: "/medical-disclaimer"},
        ]}/>
      </div>
      <div className="border-t border-white/10 py-6">
        <div className="max-w-7xl mx-auto px-6 md:px-12 text-xs text-white/50 flex flex-col md:flex-row justify-between gap-2">
          <div>© {new Date().getFullYear()} Dr. Tania Gurdip Singh. All content is provided for general education and does not replace individual medical assessment or advice.</div>
          <div className="flex gap-5">
            <Link to="/privacy">Privacy</Link>
            <Link to="/terms">Terms</Link>
            <Link to="/medical-disclaimer">Medical Disclaimer</Link>
          </div>
        </div>
      </div>
    </footer>
  );
}

function FooterColumn({ title, links }) {
  return (
    <div>
      <div className="mono text-[10px] uppercase tracking-[0.2em] text-white/50">{title}</div>
      <ul className="mt-3 space-y-2 text-sm text-white/80">
        {links.map(l => (
          <li key={l.to || l.href}>
            {l.to ? (
              <Link to={l.to} className="hover:text-prestige-soft">{l.label}</Link>
            ) : (
              <a href={l.href} className="hover:text-prestige-soft">{l.label}</a>
            )}
          </li>
        ))}
      </ul>
    </div>
  );
}
